import { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import DueNameField from "./DueNameField";
import { Input } from "./ui/input";
import { formatMoney } from "../lib/format";
import { PAYMENT_FULL, PAYMENT_LATER, PAYMENT_PART, accountNamed, leftToPay } from "../lib/dues";

const MODES = [
  { id: PAYMENT_FULL, label: "Paid" },
  { id: PAYMENT_PART, label: "Part paid" },
  { id: PAYMENT_LATER, label: "Pay later" },
];

function ShareChoice({ share, choice, accounts, onChange }) {
  const mode = choice?.mode || PAYMENT_FULL;
  const name = choice?.name ?? share.name;
  const left = leftToPay(share, choice);
  const onAccount = name.trim() ? accountNamed(accounts, name) : null;

  return (
    <div className="space-y-2" data-testid={`bill-share-${share.id}`}>
      {share.label && (
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="truncate text-zinc-300">{share.label}</span>
          <span className="shrink-0 font-medium text-zinc-100">₹{formatMoney(share.amount)}</span>
        </div>
      )}
      <div className="grid grid-cols-3 gap-1 rounded-lg border border-zinc-800 bg-zinc-950/60 p-1">
        {MODES.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            data-testid={`pay-${id}-${share.id}`}
            aria-pressed={mode === id}
            onClick={() => onChange({ ...choice, mode: id })}
            className={`h-10 rounded-md text-sm font-medium transition-colors duration-150 ${
              mode === id
                ? id === PAYMENT_FULL ? "bg-green-600 text-white" : "bg-amber-500 text-zinc-950"
                : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode !== PAYMENT_FULL && (
        <div className="space-y-2">
          {mode === PAYMENT_PART && (
            <Input
              data-testid={`paid-now-${share.id}`}
              type="number"
              inputMode="decimal"
              min="0"
              placeholder="Amount paid now"
              value={choice?.paid ?? ""}
              onChange={(e) => onChange({ ...choice, paid: e.target.value })}
              className="h-11 bg-[#09090b] border-zinc-700 text-zinc-100 placeholder:text-zinc-500"
            />
          )}
          <DueNameField
            accounts={accounts}
            value={name}
            onChange={(next) => onChange({ ...choice, name: next })}
            testId={`due-name-${share.id}`}
          />
          <p className="text-xs text-amber-400" data-testid={`due-left-${share.id}`}>
            ₹{formatMoney(left)} goes on {name.trim() ? `${name.trim()}'s` : "their"} dues
            {onAccount?.balance > 0 && `, on top of ₹${formatMoney(onAccount.balance)} already owed`}
          </p>
        </div>
      )}
    </div>
  );
}

// How a bill is being settled: in full, in part, or later on someone's name.
export default function BillPayment({ shares, choices, onChange }) {
  const { apiClient } = useApp();
  const [accounts, setAccounts] = useState([]);
  const anyOnDues = shares.some((s) => [PAYMENT_PART, PAYMENT_LATER].includes(choices[s.id]?.mode));

  // Only suggestions, so it waits until a name is being asked for and a failure
  // just means no suggestions.
  useEffect(() => {
    if (!anyOnDues) return;
    let current = true;
    apiClient.get("/dues")
      .then((res) => { if (current) setAccounts(res.data.accounts); })
      .catch(() => {});
    return () => { current = false; };
  }, [anyOnDues, apiClient]);

  return (
    <div className="space-y-3" data-testid="bill-payment">
      {shares.map((share) => (
        <ShareChoice
          key={share.id}
          share={share}
          choice={choices[share.id]}
          accounts={accounts}
          onChange={(choice) => onChange({ ...choices, [share.id]: choice })}
        />
      ))}
    </div>
  );
}
